import { ReactElement } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import Loader from '~/ui/components/common/Loader';
import EditClientAllocationForm from './EditClientAllocationForm';

import { NETWORK, VIEW_CLIENT_ALLOCATION, VIEW_CRISIS_ALLOCATION } from '~/ui/constants/paths';
import extractDateFromString from '~/utils/date/extractDateFromString';
import { getAssignedClients, getCurrentAllocations } from './helpers';
import useGetEditClientAllocations from './useGetEditClientAllocations';

type IParams = {
  actTeamId: string;
  clientAllocationDate: string;
};

interface IProps {
  isCrisis?: boolean;
}

const EditClientAllocation = ({ isCrisis = false }: IProps): ReactElement => {
  const { actTeamId, clientAllocationDate } = useParams<IParams>();
  const { pathname } = useLocation();

  const { clinic: userClinic } = useStoreState(state => state.user.current);
  const actTeam = useStoreState(state => state.actTeam.current);
  const clientAllocation = useStoreState(state => state.clientAllocation.current) || [];
  const locationGroups = useStoreState(state => state.clientAllocation.locationGroups);

  const { isActTeam, isGlobalUser } = useRole();

  const { isLoading, teamMembers, clients, assignedClients } = useGetEditClientAllocations({
    actTeamId,
    clientAllocationDate,
    isCrisis,
  });

  // Temporary solution to hide assigned clients if locations groups are selected
  const hasLocationGroupActive = clientAllocation.some(
    item => item.locationGroups?.length && !item.isArchived,
  );

  const currentAllocations = getCurrentAllocations(
    hasLocationGroupActive,
    clientAllocation,
    isCrisis,
  );

  const breadcrumbItems = [
    ...(isGlobalUser ? [{ to: NETWORK, title: `${userClinic.name} Teams` }] : []),
    ...(isActTeam
      ? [
          {
            to: {
              pathname: (isCrisis ? VIEW_CRISIS_ALLOCATION : VIEW_CLIENT_ALLOCATION)
                .replace(':actTeamId', actTeamId)
                .replace(':clientAllocationDate', String(clientAllocation[0].date.split('T')[0])),
            },
            title: 'Client Allocation',
          },
        ]
      : []),
    ...(!isActTeam && !isGlobalUser ? [{ to: NETWORK, title: `${userClinic.name} Teams` }] : []),
    { to: pathname, title: isCrisis ? 'Edit Crisis Allocation' : 'Edit Client Allocation' },
  ];

  if (isLoading || (!actTeam && !isGlobalUser) || !clientAllocation) return <Loader />;

  return (
    <div>
      <h2>{`${
        isCrisis ? 'Edit Crisis Allocation' : 'Edit Client Allocation'
      } | ${extractDateFromString(clientAllocation?.[0]?.date || '')}`}</h2>
      <Breadcrumbs itemsForBreadcrumbs={breadcrumbItems} />
      <EditClientAllocationForm
        clientAllocation={currentAllocations}
        assignedClients={getAssignedClients(currentAllocations, assignedClients)}
        teamMembers={teamMembers}
        clients={clients}
        actTeamId={actTeamId}
        clinicId={String(userClinic.id)}
        locationGroups={locationGroups}
        isLocationGroupAllocation={hasLocationGroupActive}
        isCrisis={isCrisis}
      />
    </div>
  );
};

export default EditClientAllocation;
